// src/ui/betLabel.ts
//
// Phase 21 (WR-02): Shared pure helpers for bet cell labelling.
// Extracted from Table.ts so CellPickerModal can use the same logic without
// importing Phaser (which would break vitest unit tests in jsdom/node environments).
//
// No Phaser dependency — pure TypeScript only.

import type { BetType } from '../state/types';

/**
 * Phase 21 (BOARD-21): compact label for outside-sub cells.
 * Renders the parent glyph plus an L/R half marker derived from the
 * lowest number in the sub-cell's number set (left-half = lower min).
 *
 * Used by both Table.ts and CellPickerModal.ts so both render consistent labels.
 */
export function cellLabelForSub(bet: Extract<BetType, { kind: 'outside-sub' }>): string {
  const minN = Math.min(...bet.numbers);
  // Determine left vs right: left.min < right.min for all parents.
  // Use the parent's known median as a threshold.
  const PARENT_MEDIAN: Record<string, number> = {
    low: 9, high: 27, even: 18, odd: 17, red: 18, black: 17,
  };
  const median = PARENT_MEDIAN[bet.parentKind] ?? 18;
  const isLeft = minN <= median;
  const half = isLeft ? 'L' : 'R';
  switch (bet.parentKind) {
    case 'red':   return `♦${half}`;
    case 'black': return `♠${half}`;
    case 'low':   return isLeft ? '1-9' : '10-18';
    case 'high':  return isLeft ? '19-27' : '28-36';
    case 'even':  return `E${half}`;
    case 'odd':   return `O${half}`;
    default:      return half;
  }
}
